/**
 * UsersHierarchyTab — Aba 3 "Hierarquia" (Brief 5 A1, W3.1).
 *
 * Exibe árvore reportsTo: managers chain (acima) + directs (abaixo).
 * Consome GET /v1/users/:id/hierarchy.
 *
 * Decisão executiva W3.1-D4: tree visual simples com indentation
 * (AuditTreeView DS v2.2.0 é orientado a eventos de auditoria; para
 * hierarquia organizacional, tree custom é mais adequada e P4-resistente).
 *
 * Pattern D-B: gate users-read próprio — componente é autônomo.
 */

import { useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { useUserHierarchy, useUsers } from '../../../hooks/use-users';

import { USER_ROLE_LABELS } from './types';
import { UserAvatar } from './UserAvatar';
import { UserStatusBadge } from './UserStatusBadge';

import type { UserWireItem } from './types';

function UserRow({ user, indent = 0 }: { user: UserWireItem; indent?: number }): JSX.Element {
  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-border-subtle p-3 bg-surface"
      style={{ marginLeft: `${indent * 24}px` }}
    >
      <UserAvatar id={user.id} name={user.name} size="sm" />
      <div className="flex-1">
        <p className="text-sm font-medium text-text-primary">{user.name}</p>
        <p className="text-xs text-text-secondary">
          {USER_ROLE_LABELS[user.primaryRole] ?? user.primaryRole} · {user.email}
        </p>
      </div>
      <UserStatusBadge status={user.status} />
    </div>
  );
}

interface Props {
  /** ID do usuário cujo subgraph estamos vendo. Se null → seletor. */
  defaultUserId?: string | null;
}

export function UsersHierarchyTab({ defaultUserId }: Props): JSX.Element {
  const canRead = useCapability('users-read');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(defaultUserId ?? null);

  const { data: usersData } = useUsers({ status: 'active', limit: 100 });
  const allUsers: UserWireItem[] = usersData?.data ?? [];

  const { data: hierarchy, isLoading, isError } = useUserHierarchy(selectedUserId);

  if (!canRead) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <span className="text-4xl">🔒</span>
        <p className="mt-3 text-sm font-medium text-text-secondary">
          Sem permissão para visualizar hierarquia
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Seletor de usuário */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">
          Ver hierarquia de
        </label>
        <select
          value={selectedUserId ?? ''}
          onChange={(e) => setSelectedUserId(e.target.value || null)}
          className="rounded border border-border-subtle bg-surface px-3 py-2 text-sm w-72"
          aria-label="Selecionar usuário para hierarquia"
        >
          <option value="">Selecione um usuário…</option>
          {allUsers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name} ({USER_ROLE_LABELS[u.primaryRole] ?? u.primaryRole})
            </option>
          ))}
        </select>
      </div>

      {isLoading && selectedUserId && (
        <p className="text-sm text-text-secondary">Carregando hierarquia…</p>
      )}

      {isError && (
        <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Erro ao carregar hierarquia.
        </div>
      )}

      {hierarchy && (
        <div className="flex flex-col gap-5">
          {/* Cadeia de managers */}
          {hierarchy.managersChain.length > 0 && (
            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-secondary">
                Gerentes ({hierarchy.managersChain.length})
              </h3>
              <div className="flex flex-col gap-2">
                {hierarchy.managersChain.map((u, idx) => (
                  <UserRow key={u.id} user={u} indent={idx} />
                ))}
              </div>
            </section>
          )}

          {/* Usuário selecionado */}
          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-secondary">
              Usuário
            </h3>
            <div className="rounded-lg border-2 border-indigo-300 p-1">
              <UserRow user={hierarchy.user} indent={hierarchy.managersChain.length} />
            </div>
          </section>

          {/* Reportes diretos */}
          {hierarchy.directReports.length > 0 && (
            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-secondary">
                Reportes diretos ({hierarchy.directReports.length})
              </h3>
              <div className="flex flex-col gap-2">
                {hierarchy.directReports.map((u) => (
                  <UserRow key={u.id} user={u} indent={hierarchy.managersChain.length + 1} />
                ))}
              </div>
            </section>
          )}

          {hierarchy.managersChain.length === 0 && hierarchy.directReports.length === 0 && (
            <p className="text-sm text-text-secondary">
              Este usuário não possui gerentes nem reportes configurados.
            </p>
          )}
        </div>
      )}

      {!selectedUserId && (
        <p className="text-sm text-text-secondary">
          Selecione um usuário para visualizar sua posição na hierarquia.
        </p>
      )}
    </div>
  );
}
